import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, catchError, map, of, BehaviorSubject, tap, throwError, combineLatest } from 'rxjs';
import { CriticalDate } from '../../objects/critical-dates';
import { BaseEntityClassService } from './base-entity-class.service';
import { PaginationService } from '../pagination.service';
import { ContractService } from './contract.service';
import { BaaService } from './baa.service';
import { CoiService } from './coi.service';


@Injectable({
  providedIn: 'root'
})
export class CriticalDatesService extends BaseEntityClassService {

  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);
  private contractService = inject(ContractService);
  private baaService = inject(BaaService);
  private coiService = inject(CoiService);

  private readonly headers = {
    'Content-Type': 'application/json'
  };

  // BehaviorSubjects for cache management (matching ContractService pattern)
  private allCriticalDatesSubject = new BehaviorSubject<CriticalDate[]>([]);
  public readonly allCriticalDates$: Observable<CriticalDate[]> = this.allCriticalDatesSubject.asObservable();

  private visibleCriticalDatesSubject = new BehaviorSubject<CriticalDate[]>([]);
  public readonly visibleCriticalDates$: Observable<CriticalDate[]> = this.visibleCriticalDatesSubject.asObservable();

  constructor() {
    super();
    // Auto-refresh critical dates on service construction (like ContractService)
    this.refreshAllCriticalDates();
    this.setupVisibilityFiltering();
  }

  private setupVisibilityFiltering(): void {
    combineLatest([
      this.allCriticalDates$,
      this.contractService.visibleContracts$,
      this.baaService.visibleBaas$,
      this.coiService.visibleCois$
    ]).subscribe(([allDates, visibleContracts, visibleBaas, visibleCois]) => {
      // Build Sets of visible entity IDs for fast lookup
      const visibleContractIds = new Set(visibleContracts.map(c => c.contractId));
      const visibleContractNums = new Set(visibleContracts.map(c => c.contractNum));
      const visibleBaaIds = new Set(visibleBaas.map((b: any) => b.baaId));
      const visibleBaaNums = new Set(visibleBaas.map((b: any) => b.baaNum));
      const visibleCoiIds = new Set(visibleCois.map((c: any) => c.coiId));
      const visibleCoiNums = new Set(visibleCois.map((c: any) => c.coiNum));

      const filteredDates = allDates.filter(date => {
        // Check both entity and entityType fields (data uses entityType, some legacy uses entity)
        const type = (date.entityType || date.entity || '').toLowerCase();
        const id = date.entityId || '';
        const num = date.entityNum || '';

        if (type === 'contract') {
          return visibleContractIds.has(id) || visibleContractNums.has(num);
        }
        if (type === 'baa') {
          return visibleBaaIds.has(id) || visibleBaaNums.has(num);
        }
        if (type === 'coi') {
          return visibleCoiIds.has(id) || visibleCoiNums.has(num);
        }

        // For other entity types (vendor, person, etc.), show all dates
        return true;
      });

      this.visibleCriticalDatesSubject.next(filteredDates);
    });
  }

  /**
   * Refreshes all critical dates by calling the paginated API and updating the subject.
   * Uses the pagination service to fetch all pages progressively.
   * API endpoint: POST api/{org}/page-of-dates
   */
  public refreshAllCriticalDates(): void {
    const getPageOfDates = (lastEvaluatedKey: any = null): Observable<any> => {
      const url = environment.URL_DATES_GET_PAGE_OF_ALL_DATES;
      const body = { lastEvaluatedKey };

      return this.http.post(url, body).pipe(
        map((response: any) => ({
          items: response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        })),
        catchError(error => {
          console.error('Error fetching critical dates page:', error);
          return of({ items: [], lastEvaluatedKey: null });
        })
      );
    };

    // Use the PaginationService to handle the recursive fetching of all pages.
    this.paginationService.getAllPaginatedDataProgressively(getPageOfDates)
      .pipe(
        catchError(error => {
          console.error('Error in getAllPaginatedData:', error);
          this.allCriticalDatesSubject.next([]);
          return of([]);
        })
      )
      .subscribe(allDates => {
        // Handle both response.criticalDateData and direct CriticalDate objects
        const filteredObjects = allDates.map((obj: any) => obj.criticalDateData || obj);
        this.allCriticalDatesSubject.next(filteredObjects);
      });
  }

  /**
   * Gets a fully-hydrated critical date object by its criticalDateNum.
   * NOTE: We assume critical dates are already fully hydrated when downloaded from the API.
   * This method retrieves from the in-memory cache.
   * @param criticalDateNum The critical date number to retrieve.
   * @returns An observable of the found CriticalDate or null if not found.
   */
  public getCriticalDateFullyHydrated(criticalDateNum: string): Observable<CriticalDate | null> {
    console.log('[CriticalDatesService.getCriticalDateFullyHydrated] Called with criticalDateNum:', criticalDateNum);

    return this.allCriticalDates$.pipe(
      map(dates => {
        const found = dates.find(date => date.criticalDateNum === criticalDateNum);
        console.log('[CriticalDatesService.getCriticalDateFullyHydrated] Found date:', found);
        return found || null;
      }),
      catchError(error => {
        console.error('[CriticalDatesService.getCriticalDateFullyHydrated] Error:', error);
        return of(null);
      })
    );
  }

  /**
   * Retrieves an array of dates by their date numbers.
   * This is useful when you have a specific list of dates you need to fetch.
   * API endpoint: POST api/{org}/critical-dates/batch
   * @param dateNumbers An array of strings representing the unique date numbers.
   * @returns An Observable of an array of CriticalDate objects (raw API response with criticalDateData wrapper).
   */
  getDatesByNumbers(dateNumbers: string[] | null | undefined): Observable<any[]> {
    // Check for null, undefined, or an empty array
    if (!dateNumbers || dateNumbers.length === 0) {
      console.debug('CriticalDatesService: getDatesByNumbers called with no valid date numbers. Returning an empty array.');
      return of([]); // Use 'of' to return a cold observable of an empty array
    }

    // This assumes your API has a POST endpoint that accepts an array of IDs in the body.
    return this.http.post<any[]>(environment.URL_DATES_GET_BY_NUMBERS, { dateNumbers }).pipe(
      map(dates => {
        console.log('[CriticalDatesService] getDatesByNumbers raw response:', dates);
        // Return raw response - the template expects wrapped objects with criticalDateData
        return dates || [];
      }),
      catchError(error => {
        console.error('Error fetching dates by numbers:', error);
        return of([]);
      })
    );
  }

  /**
   * Retrieves critical dates by a critical date number.
   * API endpoint: GET api/{org}/dates-by-datenum/{criticalDateNumber}
   * @param criticalDateNumber The critical date number.
   * @returns An Observable of the API response.
   */
  public getDatesByCriticalDateNum(criticalDateNumber: any): Observable<any> {
    const options = { headers: this.headers };
    return this.http.get(environment.criticalDatesByCriticalDateNumUrl + criticalDateNumber, options)
      .pipe(
        catchError(error => {
          console.error('An error occurred while fetching critical dates by number:', error);
          return of(null);
        })
      );
  }

  /**
   * Retrieves critical dates by an entity ID (parentEntityId).
   *
   * API: GET api/{org}/critical-dates/by-entity/{entityId}
   * Returns flat date objects (no wrapper)
   *
   * @param parentEntityId The ID of the parent entity (e.g., contractId)
   * @returns An Observable of CriticalDate array.
   */
  public getDatesByEntityId(parentEntityId: string): Observable<CriticalDate[]> {
    // URL already has trailing slash, just append the entityId
    const url = environment.criticalDatesByEntityIdUrl + parentEntityId;

    return this.http.get<any>(url, { headers: this.headers }).pipe(
      map((response: any) => {
        // Handle both array and wrapped formats
        if (Array.isArray(response)) {
          return response.map(item => item.criticalDateData || item);
        }
        if (response?.items) {
          return response.items.map((item: any) => item.criticalDateData || item);
        }
        return [];
      }),
      catchError(error => {
        console.error('An error occurred while fetching critical dates by entity ID:', error);
        return of([]);
      })
    );
  }

  /**
   * Adds a new critical date.
   *
   * NEW API (Phase 1):
   * - POST api/{org}/critical-dates
   * - Body: flat data (no wrapper)
   * - If no criticalDateId: CREATE - backend generates ID (format CD-xxx)
   * - Returns { criticalDateId, message }
   *
   * @param criticalDateObject The critical date object to add.
   * @returns An Observable of the API response.
   */
  public addCriticalDate(criticalDateObject: CriticalDate): Observable<any> {
    const options = { headers: this.headers };

    // NEW API: POST with flat data (no wrapper)
    return this.http.post(environment.criticalDatesAddDateUrl, criticalDateObject, options)
      .pipe(
        tap(() => {
          // Refresh cache after successful write
          this.refreshAllCriticalDates();
        }),
        catchError(error => {
          console.error('An error occurred while adding a critical date:', error);
          return throwError(() => new Error('Could not add critical date.'));
        })
      );
  }

  /**
   * Creates a new critical date entry in the database.
   *
   * NEW API (Phase 1):
   * - POST api/{org}/critical-dates
   * - Body: flat data with parentEntityId (e.g., contractId)
   * - Backend generates criticalDateId (format CD-xxx)
   * - Returns { criticalDateId, message }
   *
   * @param criticalDateObject The complete critical date object to create.
   * @returns An Observable of the created critical date with backend-generated ID.
   */
  public createCriticalDate(criticalDateObject: CriticalDate): Observable<CriticalDate> {
    const options = { headers: this.headers };

    // NEW API: POST with flat data (no wrapper)
    // For CREATE: omit criticalDateId (backend generates it)
    const payload = { ...criticalDateObject };
    delete (payload as any).criticalDateNum; // Remove old-style ID field

    return this.http.post<any>(environment.criticalDatesAddDateUrl, payload, options)
      .pipe(
        map(response => {
          // NEW API returns { criticalDateId, message }
          // Merge the backend-generated ID with the original object
          return {
            ...criticalDateObject,
            criticalDateId: response.criticalDateId
          };
        }),
        tap(() => {
          // Refresh cache after successful write
          this.refreshAllCriticalDates();
        }),
        catchError(error => {
          console.error('An error occurred while creating a critical date:', error);
          return throwError(() => new Error('Could not create critical date.'));
        })
      );
  }

  /**
   * Asks the backend to send the calendar (.ics) notification for a saved Important Date.
   *
   * Call it UNCONDITIONALLY after a save — do NOT pre-check
   * `calendarNotificationEnabled` or try to work out whether anything changed. The
   * endpoint re-reads the row and decides, comparing a `calendarFingerprint` of the
   * recipient-visible fields (eventDate, name/type, description/requiredAction,
   * recipients). That is deliberate: the form does not reliably hold the pre-edit
   * values, so any diff computed here would be guesswork, and a duplicate call is
   * idempotent server-side.
   *
   * A no-op is a **200 with a `reason`**, never a 204 — `unchanged`,
   * `calendarNotificationEnabled is not true`, `actionCompleted`, `no recipients`.
   * So a 200 is always success, and only a non-2xx is worth logging. Errors are 400
   * (unknown org / missing params), 404 (no such date), 422 (unbuildable eventDate),
   * 500 (read failure).
   *
   * Unauthenticated by design (no authorizer on that gateway). The auth interceptor
   * attaches an ID token to every execute-api host anyway; the backend ignores it.
   *
   * Returns a cold observable that NEVER errors — the caller is fire-and-forget and a
   * calendar send must not fail a save that already succeeded. Resolves to null when
   * the org has no `URL_CRITICAL_DATE_CALENDAR` configured, which is how an org opts
   * out: the key is read through `environment as any`, so a missing one is not a
   * compile error.
   */
  public sendCalendarNotification(criticalDateId: string): Observable<any | null> {
    const base = (environment as any).URL_CRITICAL_DATE_CALENDAR;
    if (!base || !criticalDateId) return of(null);

    return this.http.post<any>(base + encodeURIComponent(criticalDateId), {}, { headers: this.headers })
      .pipe(
        tap(res => {
          if (res?.reason) {
            console.log(`[CriticalDates] calendar not sent for ${criticalDateId}: ${res.reason}`);
          } else {
            console.log(`[CriticalDates] calendar sent for ${criticalDateId}`, res);
          }
        }),
        catchError(err => {
          console.error(`[CriticalDates] calendar notification failed for ${criticalDateId}:`, err?.status, err?.error);
          return of(null);
        })
      );
  }

  // ── SANDBOX-ONLY reminder test tool ───────────────────────────────────────
  // Backed by hc-all-orgs-scheduled-ops/sandbox-reminder-test.js. Only
  // environment.sandbox-ng.ts sets URL_SANDBOX_REMINDER_TEST; everywhere else
  // these resolve to null without a request. The backend has no org parameter
  // and requires a sandbox-pool login, so it cannot touch another org even if
  // called directly. Unlike sendCalendarNotification these surface errors as
  // { error } — a tester needs to see why something didn't happen.

  /** Will this date's reminder send, when, to whom — or why not. Sends nothing. */
  public getReminderTestStatus(criticalDateId: string): Observable<any | null> {
    return this.sandboxReminderTest('reminder-status/' + encodeURIComponent(criticalDateId), {});
  }

  /** Send this date's reminder now, marked [SANDBOX TEST], to internal staff only. Changes nothing on the date. */
  public sendTestReminder(criticalDateId: string): Observable<any | null> {
    return this.sandboxReminderTest('send-reminder/' + encodeURIComponent(criticalDateId), {});
  }

  /** Run the nightly reminder selection as if today were `date` (YYYY-MM-DD); optionally test-send the result. */
  public simulateReminderDay(date: string, send: boolean): Observable<any | null> {
    return this.sandboxReminderTest('simulate-day', { date, send });
  }

  private sandboxReminderTest(path: string, body: any): Observable<any | null> {
    const base = (environment as any).URL_SANDBOX_REMINDER_TEST;
    if (!base) return of(null);
    return this.http.post<any>(base + path, body, { headers: this.headers }).pipe(
      catchError(err => of({
        error: err?.error?.error || err?.error?.message ||
               (err?.status === 401 || err?.status === 403 ? 'Not signed in to sandbox — sign out and back in.' : null) ||
               `Request failed (${err?.status ?? 'network'})`
      }))
    );
  }

  /**
   * Updates a critical date (partial update).
   *
   * NEW API (Phase 1):
   * - POST api/{org}/critical-dates
   * - Body: flat data with criticalDateId + fields to update
   * - Only sent fields are modified (partial update)
   *
   * @param criticalDateId The critical date ID to update.
   * @param attribute The attribute name to update.
   * @param attributeValue The new value for the attribute.
   * @returns An observable of the API response.
   */
  public updateDateAttribute(criticalDateId: string, attribute: string, attributeValue: any): Observable<any> {
    const url = environment.criticalDatesAddDateUrl;

    // NEW API: POST with flat data containing ID + fields to update
    const updateBody = {
      criticalDateId: criticalDateId,
      [attribute]: attributeValue
    };

    const options = { headers: this.headers };

    return this.http.post(url, updateBody, options).pipe(
      tap(() => this.refreshAllCriticalDates()), // Refresh cache after update
      catchError(error => {
        console.error('Error updating critical date attribute:', error);
        return throwError(() => new Error('Could not update critical date attribute.'));
      })
    );
  }

  /**
   * Deletes a critical date by its number.
   * @param criticalDateNum The critical date number to delete.
   * @returns An Observable of the deletion result.
   */
  // public deleteCriticalDate(criticalDateNum: string): Observable<any> {
  //   const options = { headers: this.headers };
    
  //   return this.http.delete(environment.criticalDatesDeleteUrl + criticalDateNum, options)
  //     .pipe(
  //       catchError(error => {
  //         console.error('An error occurred while deleting a critical date:', error);
  //         return EMPTY;
  //       })
  //     );
  // }

  /**
   * Retrieves a page of all critical dates and returns them as a list.
   * API endpoint: POST api/{org}/page-of-dates
   * @param lastEvaluatedKey The pagination key from the previous page (null for first page).
   * @returns An Observable of the paginated response with items and lastEvaluatedKey.
   */
  public getPageOfAllDates(lastEvaluatedKey: any = null): Observable<any> {
    const url = environment.URL_DATES_GET_PAGE_OF_ALL_DATES;
    const body = { lastEvaluatedKey };
    const options = { headers: this.headers };

    return this.http.post<any>(url, body, options)
      .pipe(
        map(response => ({
          items: response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        })),
        catchError(error => {
          console.error('An error occurred while fetching a page of all critical dates:', error);
          return of({ items: [], lastEvaluatedKey: null });
        })
      );
  }

  // NOTE: The dateFormatter has been moved to a new service called DateHandlingService.
  // We can still use it here by injecting the new service:
  // this.dateHandlingService.dateFormatter(params);
}